import React from 'react';
import { ScanLabelConnectorApollo, withMobileAppMode } from '@front-activerse/common-components';
import { useLocation } from 'react-router-dom';
import TrackerScanLabelContainer from './TrackerScanLabelContainer';
import TrackerNoAccess from '../NoAccess/TrackerNoAccess';
import { hasAiAccess, hasAnyPaidAccess } from '../helpers';

interface TrackerScanLabelPageProps {
  mobileAppMode: boolean;
  // Per-brand Firestore helpers, passed in the same way the notification alerts receive them —
  // each brand owns its own Firebase app, so these cannot be imported from the shared lib.
  getClosedNotifications?: (collectionName: string, userId: number) => Promise<number[]>;
  markNotificationAsClosed?: (
    collectionName: string,
    userId: number,
    notificationId: number
  ) => Promise<void>;
}

const TrackerScanLabelPage: React.FC<TrackerScanLabelPageProps> = ({
  mobileAppMode,
  getClosedNotifications,
  markNotificationAsClosed,
}) => {
  const location = useLocation();
  const fromAutoScan = (location.state as { fromAutoScan?: boolean } | null)?.fromAutoScan ?? false;

  // Read from localStorage rather than Apollo's cache: `UserDataQuery` is keyed on a `$date`
  // variable this screen does not have, so `readQuery` would miss unless the cache happened to hold
  // that exact day. `UserDataConnectorApollo` writes this key, and `AuthLoginByTokenComponent`
  // awaits that call before rendering any route — so it is set by the time this screen mounts,
  // including a cold native-webview launch, where the token arrives from the cookie (max-age one
  // year) or from the shell's `response_app_info` message.
  const userId = Number(localStorage.getItem('userId')) || 0;

  // The required plan depends on how the user got here, because the two entry points promise
  // different things:
  //
  // - From the action sheet (`fromAutoScan`, set by the tracker sheet and the user-meals list): the
  //   entry carries the AI badge and an AI-keyed "get access" pill, so it requires the AI plan. The
  //   backend would serve any paid plan, which makes this a product decision about that entry point
  //   rather than a security boundary.
  // - From the add-product form: scanning only fills in the form's fields, so any paid access is
  //   enough. Gating this path on the AI plan would send a diet-only subscriber to a paywall in the
  //   middle of a form they are allowed to submit by hand anyway.
  //
  // An earlier revision let the add-product form through regardless of access, to avoid a dead end
  // where the form's own "Scan label" button led to the paywall and lost the typed values. Now that
  // the backend refuses the mutation, that bypass would only trade the dead end for a failure after
  // both photos were taken — so the form's gated button routes here to show the access screen with
  // a back button instead (see TrackerAddUserProductContainer).
  const hasRequiredAccess = fromAutoScan ? hasAiAccess() : hasAnyPaidAccess();

  if (hasRequiredAccess || mobileAppMode) {
    return (
      <ScanLabelConnectorApollo>
        {({ cancelScanLabel }) => (
          <TrackerScanLabelContainer
            key={location.key}
            cancelScanLabel={cancelScanLabel}
            mobileAppMode={mobileAppMode}
            userId={userId}
            getClosedNotifications={getClosedNotifications}
            markNotificationAsClosed={markNotificationAsClosed}
          />
        )}
      </ScanLabelConnectorApollo>
    );
  }

  // Always offer a way back. Both entries have somewhere to return to: from the add-product form the
  // form itself is still on the stack holding the typed-in values, and from the action sheet the
  // tracker is. This used to be conditional on the form entry, on the assumption that a sheet entry
  // had no origin — but the sheet opens over the tracker, so a paywall with no way out was a dead
  // end reachable in one tap.
  return <TrackerNoAccess showBackBtn={true} />;
};

export default withMobileAppMode(TrackerScanLabelPage);
